/**
 * 甘特图工具栏组件
 *
 * 提供缩放级别切换、筛选器、导航按钮等功能
 * 支持工作流阶段筛选、日期范围预设、自动适配
 */

import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  GitBranch,
  Layers,
  Crosshair,
} from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { DateJumpPicker } from './date-jump-picker'
import { WORKFLOW_STAGE_LABELS, DATE_RANGE_PRESETS } from './types'

import type {
  ZoomLevel,
  GanttFilters,
  GroupByOption,
  WorkflowStage,
  DateRangePreset,
} from './types'
import type { IssueStatus, IssuePriority , DeveloperGroup } from '@/hooks'

interface GanttToolbarProps {
  /** 当前缩放级别 */
  zoomLevel: ZoomLevel
  /** 缩放级别变更回调 */
  onZoomChange: (level: ZoomLevel) => void
  /** 当前筛选条件 */
  filters: GanttFilters
  /** 筛选条件变更回调 */
  onFiltersChange: (filters: GanttFilters) => void
  /** 分组方式 */
  groupBy: GroupByOption
  /** 分组方式变更回调 */
  onGroupByChange: (groupBy: GroupByOption) => void
  /** 今日按钮点击回调 */
  onTodayClick: () => void
  /** 导航回调 */
  onNavigate: (direction: 'prev' | 'next') => void
  /** 日期跳转回调 */
  onDateJump?: (date: Date) => void
  /** 项目开始日期 */
  projectStartDate?: Date
  /** 项目结束日期 */
  projectEndDate?: Date
  /** 当前显示日期 */
  currentDate?: Date
  /** 显示依赖线 */
  showDependencies: boolean
  /** 依赖线显示切换回调 */
  onToggleDependencies: () => void
  /** 可见任务数 */
  visibleCount: number
  /** 总任务数 */
  totalCount: number
  /** 日期范围预设 */
  dateRangePreset?: DateRangePreset
  /** 日期范围预设变更回调 */
  onDateRangePresetChange?: (preset: DateRangePreset) => void
  /** 自动适配按钮点击回调 */
  onAutoFitClick?: () => void
  /** 是否有任务在视图外 */
  hasTasksOutsideView?: boolean
}

export function GanttToolbar({
  zoomLevel,
  onZoomChange,
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  onTodayClick,
  onNavigate,
  onDateJump,
  projectStartDate,
  projectEndDate,
  currentDate,
  showDependencies,
  onToggleDependencies,
  visibleCount,
  totalCount,
  dateRangePreset,
  onDateRangePresetChange,
  onAutoFitClick,
  hasTasksOutsideView,
}: GanttToolbarProps) {
  const hasActiveFilters = React.useMemo(() => {
    return (
      filters.assigneeId ||
      (filters.status && filters.status.length > 0) ||
      (filters.priority && filters.priority.length > 0) ||
      filters.developerGroup ||
      filters.milestoneId ||
      filters.workflowStage ||
      filters.isOverdue ||
      filters.isRolledBack
    )
  }, [filters])

  // 获取当前预设的显示标签
  const currentPresetLabel = React.useMemo(() => {
    if (!dateRangePreset) {return '自定义'}
    if (dateRangePreset === 'auto') {return '全部任务'}
    if (dateRangePreset === 'custom') {return '自定义'}
    return DATE_RANGE_PRESETS[dateRangePreset]?.label || '自定义'
  }, [dateRangePreset])

  return (
    <div className="flex items-center justify-between border-b bg-background px-4 py-2">
      {/* 左侧：缩放和导航 */}
      <div className="flex items-center gap-2">
        {/* 缩放级别选择 */}
        <Select value={zoomLevel} onValueChange={(v) => onZoomChange(v as ZoomLevel)}>
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">日</SelectItem>
            <SelectItem value="week">周</SelectItem>
            <SelectItem value="month">月</SelectItem>
          </SelectContent>
        </Select>

        {/* 日期范围预设选择器 */}
        {onDateRangePresetChange && (
          <Select
            value={dateRangePreset || 'custom'}
            onValueChange={(v) => onDateRangePresetChange(v as DateRangePreset)}
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder={currentPresetLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">本周</SelectItem>
              <SelectItem value="month">本月</SelectItem>
              <SelectItem value="quarter">本季度</SelectItem>
              <SelectItem value="year">本年</SelectItem>
              <SelectItem value="auto">全部任务</SelectItem>
              {dateRangePreset === 'custom' && (
                <SelectItem value="custom">自定义</SelectItem>
              )}
            </SelectContent>
          </Select>
        )}

        {/* 导航按钮 */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('prev')}
            title="上一个"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onTodayClick}
            className="px-3"
          >
            <Calendar className="mr-1.5 h-4 w-4" />
            今日
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('next')}
            title="下一个"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* 自动适配按钮 */}
        {onAutoFitClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={hasTasksOutsideView ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={onAutoFitClick}
                  className={cn(
                    'px-3',
                    hasTasksOutsideView && 'bg-primary/10 text-primary animate-pulse'
                  )}
                >
                  <Crosshair className="mr-1.5 h-4 w-4" />
                  定位到任务
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>自动调整视图以显示所有任务</p>
                {hasTasksOutsideView && (
                  <p className="text-xs text-muted-foreground">有任务在当前视图外</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* 日期跳转选择器 (Requirement 11.1, 11.2, 11.3) */}
        {onDateJump && (
          <DateJumpPicker
            currentDate={currentDate ?? new Date()}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            onDateSelect={onDateJump}
          />
        )}
      </div>

      {/* 中间：任务计数 */}
      <div className="text-sm text-muted-foreground">
        {visibleCount === totalCount ? (
          <span>{totalCount} 个任务</span>
        ) : (
          <span>
            显示 {visibleCount} / {totalCount} 个任务
          </span>
        )}
      </div>

      {/* 右侧：筛选和分组 */}
      <div className="flex items-center gap-2">
        {/* 依赖线切换 */}
        <Button
          variant={showDependencies ? 'secondary' : 'ghost'}
          size="sm"
          onClick={onToggleDependencies}
          title={showDependencies ? '隐藏依赖线' : '显示依赖线'}
        >
          <GitBranch className="mr-1.5 h-4 w-4" />
          依赖
        </Button>

        {/* 分组选择 */}
        <Select value={groupBy} onValueChange={(v) => onGroupByChange(v as GroupByOption)}>
          <SelectTrigger className="w-28">
            <Layers className="mr-1.5 h-4 w-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">不分组</SelectItem>
            <SelectItem value="milestone">按里程碑</SelectItem>
            <SelectItem value="assignee">按负责人</SelectItem>
            <SelectItem value="developerGroup">按开发组</SelectItem>
          </SelectContent>
        </Select>

        {/* 筛选器 */}
        <FilterPopover
          filters={filters}
          onFiltersChange={onFiltersChange}
          hasActiveFilters={!!hasActiveFilters}
        />
      </div>
    </div>
  )
}

// ==================== 子组件 ====================

interface FilterPopoverProps {
  filters: GanttFilters
  onFiltersChange: (filters: GanttFilters) => void
  hasActiveFilters: boolean
}

function FilterPopover({
  filters,
  onFiltersChange,
  hasActiveFilters,
}: FilterPopoverProps) {
  const [open, setOpen] = React.useState(false)

  const handleClearFilters = () => {
    onFiltersChange({})
  }

  const handleStatusChange = (status: IssueStatus, checked: boolean) => {
    const currentStatuses = filters.status || []
    const newStatuses = checked
      ? [...currentStatuses, status]
      : currentStatuses.filter((s) => s !== status)
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    })
  }

  const handlePriorityChange = (priority: IssuePriority, checked: boolean) => {
    const currentPriorities = filters.priority || []
    const newPriorities = checked
      ? [...currentPriorities, priority]
      : currentPriorities.filter((p) => p !== priority)
    onFiltersChange({
      ...filters,
      priority: newPriorities.length > 0 ? newPriorities : undefined,
    })
  }

  const handleDeveloperGroupChange = (group: DeveloperGroup | undefined) => {
    onFiltersChange({
      ...filters,
      developerGroup: group,
    })
  }

  const handleWorkflowStageChange = (stage: WorkflowStage | undefined) => {
    onFiltersChange({
      ...filters,
      workflowStage: stage,
    })
  }

  const handleOverdueChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      isOverdue: checked || undefined,
    })
  }

  const handleRolledBackChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      isRolledBack: checked || undefined,
    })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={hasActiveFilters ? 'secondary' : 'ghost'}
          size="sm"
          className={cn(hasActiveFilters && 'bg-primary/10')}
        >
          <Filter className="mr-1.5 h-4 w-4" />
          筛选
          {hasActiveFilters && (
            <span className="ml-1.5 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
              !
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">筛选条件</h4>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-auto px-2 py-1 text-xs"
              >
                清除全部
              </Button>
            )}
          </div>

          {/* 工作流阶段筛选 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">工作流阶段</label>
            <Select
              value={filters.workflowStage || 'all'}
              onValueChange={(v) =>
                handleWorkflowStageChange(v === 'all' ? undefined : (v as WorkflowStage))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="全部阶段" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部阶段</SelectItem>
                {WORKFLOW_STAGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 工作流状态筛选 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">工作流状态</label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="overdue-filter" className="text-sm font-normal">
                  仅显示超时任务
                </Label>
                <Switch
                  id="overdue-filter"
                  checked={filters.isOverdue || false}
                  onCheckedChange={handleOverdueChange}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="rollback-filter" className="text-sm font-normal">
                  仅显示回退任务
                </Label>
                <Switch
                  id="rollback-filter"
                  checked={filters.isRolledBack || false}
                  onCheckedChange={handleRolledBackChange}
                />
              </div>
            </div>
          </div>

          {/* 状态筛选 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">状态</label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  label={option.label}
                  checked={filters.status?.includes(option.value) || false}
                  onChange={(checked) => handleStatusChange(option.value, checked)}
                />
              ))}
            </div>
          </div>

          {/* 优先级筛选 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">优先级</label>
            <div className="flex flex-wrap gap-1.5">
              {PRIORITY_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  label={option.label}
                  checked={filters.priority?.includes(option.value) || false}
                  onChange={(checked) => handlePriorityChange(option.value, checked)}
                />
              ))}
            </div>
          </div>

          {/* 开发组筛选 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">开发组</label>
            <Select
              value={filters.developerGroup || 'all'}
              onValueChange={(v) =>
                handleDeveloperGroupChange(v === 'all' ? undefined : (v as DeveloperGroup))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {DEVELOPER_GROUP_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface FilterChipProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

function FilterChip({ label, checked, onChange }: FilterChipProps) {
  return (
    <button
      type="button"
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background hover:bg-accent'
      )}
      onClick={() => onChange(!checked)}
    >
      {label}
    </button>
  )
}

// ==================== 常量 ====================

const STATUS_OPTIONS: Array<{ value: IssueStatus; label: string }> = [
  { value: 'backlog', label: '待办' },
  { value: 'todo', label: '待开始' },
  { value: 'in_progress', label: '进行中' },
  { value: 'code_review', label: '代码审查' },
  { value: 'testing', label: '测试中' },
  { value: 'done', label: '已完成' },
  { value: 'blocked', label: '已阻塞' },
]

const PRIORITY_OPTIONS: Array<{ value: IssuePriority; label: string }> = [
  { value: 'urgent', label: '紧急' },
  { value: 'high', label: '高' },
  { value: 'medium', label: '中' },
  { value: 'low', label: '低' },
]

const DEVELOPER_GROUP_OPTIONS: Array<{ value: DeveloperGroup; label: string }> = [
  { value: 'product', label: '产品' },
  { value: 'design', label: '设计' },
  { value: 'frontend', label: '前端' },
  { value: 'backend', label: '后端' },
  { value: 'testing', label: '测试' },
]

const WORKFLOW_STAGE_OPTIONS: Array<{ value: WorkflowStage; label: string }> = Object.entries(
  WORKFLOW_STAGE_LABELS
).map(([value, label]) => ({
  value: value as WorkflowStage,
  label,
}))
